import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageProps, User } from "../types/auth";
import { trackPageVisit } from "../utils/pageTracker";

// Import SVG icons
import CommentAltIcon from "../assets/icons/comment-alt.svg";
import GraduationCapIcon from "../assets/icons/graduation-cap.svg";
import FileMedicalAltIcon from "../assets/icons/file-medical-alt.svg";
import CalendarIcon from "../assets/icons/calendar.svg";
import TrophyStarIcon from "../assets/icons/trophy-star.svg";
import ListCheckIcon from "../assets/icons/list-check.svg";

import styles from './styles/MemberDetailsPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Member extends User {
  role: string;
}

interface Curriculum {
  curriculum_id: number;
  name: string;
}

interface Level {
  level_id: number;
  name: string;
  sequence: number;
}

interface Skill {
  skill_id: number;
  name: string;
  status: "complete" | "incomplete";
  completed_at?: string;
  actor_username?: string;
}

interface Accomplishment {
  ag_id: number;
  name: string;
}

interface LevelDetails {
  enrolled_date?: string;
  completed_date?: string;
  skills: Skill[];
  accomplishments: Accomplishment[];
  attendances?: number;
  skills_count: number;
  completed_skills_count: number;
}

interface Note {
  note_id: number;
  org_id: number;
  user_id: number;
  note: string;
  created_at: string;
  author_username: string;
}

interface RouteParams {
  [key: string]: string | undefined;
  member_id?: string;
}

interface LevelSequence {
  level_id: number;
  name: string;
  sequence: number;
}

const MemberDetailsPage: React.FC<PageProps> = ({ user, token, selectedOrgID }) => {
  const { member_id } = useParams<RouteParams>();
  const [memberInfo, setMemberInfo] = useState<Member | null>(null);
  const [enrolledCurriculums, setEnrolledCurriculums] = useState<Curriculum[]>([]);
  const [selectedCurriculum, setSelectedCurriculum] = useState<number | null>(null);
  const [levels, setLevels] = useState<Level[]>([]);
  const [activeLevelID, setActiveLevelID] = useState<number | null>(null);
  const [levelDetails, setLevelDetails] = useState<Record<number, LevelDetails>>({});
  const [notes, setNotes] = useState<Note[]>([]);
  const [mode, setMode] = useState<"owner/instructor" | "member">("owner/instructor");
  const [error, setError] = useState<string>("");
  const hasTrackedRef = useRef(false);
  const navigate = useNavigate();

  // Track page visit once we have member info
  useEffect(() => {
    if (memberInfo && !hasTrackedRef.current) {
      const displayName = memberInfo.display_name || memberInfo.username;
      trackPageVisit(`Member: ${displayName}`, `/members/${member_id}`);
      hasTrackedRef.current = true;
    }
  }, [memberInfo, member_id]);

  useEffect(() => {
    const determineMode = () => {
      if (user?.organizations) {
        const organization = user.organizations.find(org => org.org_id === Number(selectedOrgID));
        if (organization?.role === "member") {
          setMode("member");
        } else {
          setMode("owner/instructor");
        }
      }
    };

    determineMode();
  }, [user, selectedOrgID]);

  useEffect(() => {
    const fetchMemberDetails = async () => {
      try {
        const memberResponse = await fetch(`${API_BASE_URL}/api/users/${member_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!memberResponse.ok) {
          const errorData = await memberResponse.json();
          setError(errorData.error || "Failed to fetch member details.");
          return;
        }

        const memberData: Member = await memberResponse.json();
        setMemberInfo(memberData || null);

        const curriculumResponse = await fetch(`${API_BASE_URL}/api/users/${member_id}/curriculums`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!curriculumResponse.ok) {
          const errorData = await curriculumResponse.json();
          setError(errorData.error || "Failed to fetch curriculums.");
          return;
        }

        const curriculumData: Curriculum[] = await curriculumResponse.json();
        setEnrolledCurriculums(Array.isArray(curriculumData) ? curriculumData : []);
        const numCurriculums = curriculumData?.length ?? 0;
        if (numCurriculums === 1) {
          setSelectedCurriculum(curriculumData[0].curriculum_id);
        }
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    if (member_id) {
      fetchMemberDetails();
    }
  }, [member_id, token, selectedOrgID]);

  const handleCurriculumSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const curriculumID = parseInt(event.target.value, 10);
    setSelectedCurriculum(curriculumID);
  };

  useEffect(() => {
    const fetchLevels = async () => {
      if (!selectedCurriculum) return;

      try {
        const response = await fetch(`${API_BASE_URL}/api/users/${member_id}/curriculums/${selectedCurriculum}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch curriculum levels.");
          return;
        }

        const data = await response.json();
        const sortedLevels = Array.isArray(data.levels_sequence)
          ? (data.levels_sequence as LevelSequence[]).sort((a, b) => a.sequence - b.sequence)
          : [];
        setLevels(sortedLevels);

        if (data.active_level_id) {
          setActiveLevelID(data.active_level_id);
          fetchLevelDetails(data.active_level_id);
        }
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    fetchLevels();
  }, [selectedCurriculum, token, selectedOrgID, member_id]);

  const fetchLevelDetails = async (levelID: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${member_id}/levels/${levelID}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });
      const details: LevelDetails = await response.json();
      if (!details || typeof details !== "object") {
        setLevelDetails((prev) => ({ 
          ...prev, 
          [levelID]: { 
            skills: [], 
            accomplishments: [], 
            skills_count: 0,
            completed_skills_count: 0
          } 
        }));
        return;
      }
      setLevelDetails((prev) => ({ ...prev, [levelID]: details }));
    } catch {
      console.error("Failed to fetch level details.");
    }
  };

  const handleSkillToggle = async (levelID: number, skillID: number, completed: boolean) => {
    const userID = parseInt(member_id || "0", 10);
    try {
      const url = completed
        ? `${API_BASE_URL}/api/levels/${levelID}/skills/${skillID}/users`
        : `${API_BASE_URL}/api/levels/${levelID}/skills/${skillID}/users/${userID}`;
  
      const method = completed ? "POST" : "DELETE";
      const body = completed ? JSON.stringify({ user_id: userID }) : undefined;

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
          "Content-Type": "application/json",
        },
        body,
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update skill status.");
      }

      setLevelDetails((prev: Record<number, LevelDetails>): Record<number, LevelDetails> => {
        const updatedLevel = prev[levelID];
        if (!updatedLevel) return prev;

        const newState: Record<number, LevelDetails> = {
          ...prev,
          [levelID]: {
            ...updatedLevel,
            completed_skills_count: updatedLevel.completed_skills_count + (completed ? 1 : -1),
            skills: updatedLevel.skills.map(skill => 
              skill.skill_id === skillID 
                ? { ...skill, status: completed ? "complete" : "incomplete" }
                : skill
            )
          }
        };
        return newState;
      });
    } catch (err) {
      if (err instanceof Error) {
        console.error("Error updating skill status:", err.message);
      }
    }
  };

  const handleRevokeAccomplishment = async (agID: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/accomplishments/${agID}/user/${member_id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to revoke accomplishment.");
      }
  
      if (activeLevelID) {
        fetchLevelDetails(activeLevelID);
      }
    } catch (err) {
      if (err instanceof Error) {
        console.error("Error revoking accomplishment:", err.message);
      }
    }
  };

  const getActiveLevelSkills = (): string[] => {
    if (!activeLevelID || !levelDetails[activeLevelID]) {
      return [];
    }
    return levelDetails[activeLevelID].skills.map(skill => skill.name);
  };

  useEffect(() => {
    const fetchNotes = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/notes?user_id=${member_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          console.error("Failed to fetch notes.");
          return;
        }

        const data = await response.json();
        setNotes(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("An unexpected error occurred fetching notes.");
      }
    };

    if (member_id) {
      fetchNotes();
    }
  }, [member_id, token, selectedOrgID]);

  const formatDate = (dateString: string): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  };

  if (error) {
    return <p className={styles.errorMessage}>{error}</p>;
  }

  if (!memberInfo) {
    return <p className={styles.loadingMessage}>Loading member details...</p>;
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Member Details</h1>
      <div className={styles.memberName}>
        <strong>Member:</strong>{" "}
        {memberInfo.display_name ? `${memberInfo.display_name} (${memberInfo.username})` : memberInfo.username}
      </div>

      <div className={styles.curriculumSelect}>
        <strong>Curriculum:</strong>
        <select className={styles.select} value={selectedCurriculum?.toString() || ""} onChange={handleCurriculumSelect}>
          <option value="" disabled>Select a curriculum</option>
          {enrolledCurriculums.map((c) => (
            <option key={c.curriculum_id} value={c.curriculum_id}>{c.name}</option>
          ))}
        </select>
      </div>

      {mode === "owner/instructor" && (
        <div className={styles.actionsSection}>
          <h4 className={styles.actionsTitle}>Actions</h4>
          <div className={styles.actionsColumns}>
            <div className={styles.actionsColumn}>
              <ul className={styles.actionsList}>
                <li className={styles.actionItem}>
                  <a
                    className={styles.actionLink}
                    href={`/members/${member_id}/notes`}
                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      e.preventDefault();
                      navigate(`/members/${member_id}/notes`, {
                        state: { selectedCurriculumId: selectedCurriculum }
                      });
                    }}
                  >
                    <img src={CommentAltIcon} alt="" className={styles.actionIcon} />
                    Notes
                  </a>
                </li>
                <li className={styles.actionItem}>
                  <a className={styles.actionLink} href={`/members/${member_id}/enroll`}>
                    <img src={GraduationCapIcon} alt="" className={styles.actionIcon} />
                    Curriculums
                  </a>
                </li>
              </ul>
            </div>
            <div className={styles.actionsColumn}>
              {selectedCurriculum && (
                <ul className={styles.actionsList}>
                  <li className={styles.actionItem}>
                    <a className={styles.actionLink} href={`/members/${member_id}/snippets`}>
                      <img src={FileMedicalAltIcon} alt="" className={styles.actionIcon} />
                      Snippets
                    </a>
                  </li>
                  <li className={styles.actionItem}>
                    <a
                      className={styles.actionLink}
                      href="#"
                      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                        e.preventDefault();
                        navigate(`/members/${member_id}/attendance`, {
                          state: { selectedCurriculum },
                        });
                      }}
                    >
                      <img src={CalendarIcon} alt="" className={styles.actionIcon} />
                      Attendance
                    </a>
                  </li>
                  <li className={styles.actionItem}>
                    <a
                      className={styles.actionLink}
                      href="#"
                      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                        e.preventDefault();
                        navigate(`/members/${member_id}/skills`, {
                          state: { selectedCurriculum },
                        });
                      }}
                    >
                      <img src={ListCheckIcon} alt="" className={styles.actionIcon} />
                      Levels/Skills
                    </a>
                  </li>
                  <li className={styles.actionItem}>
                    <a className={styles.actionLink} href={`/members/${member_id}/accomplishment`}>
                      <img src={TrophyStarIcon} alt="" className={styles.actionIcon} />
                      Accomplishments
                    </a>
                  </li>
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedCurriculum && levels.length > 0 && (
        <div className={styles.levelSection}>
          {activeLevelID && levelDetails[activeLevelID] ? (
            <div className={styles.detailsPane}>
              <table className={styles.detailsTable}>
                <tbody>
                  <tr>
                    <td className={styles.labelCell}>
                      <strong>Current Level:</strong>
                    </td>
                    <td className={styles.valueCell}>
                      {levels.find(level => level.level_id === activeLevelID)?.name || "Active Level"}
                      {levelDetails[activeLevelID]?.enrolled_date && (
                        <span> (started {new Date(levelDetails[activeLevelID]?.enrolled_date).toLocaleDateString()})</span>
                      )}
                    </td>
                  </tr>
                  {levelDetails[activeLevelID]?.accomplishments &&
                   levelDetails[activeLevelID]?.accomplishments.length > 0 && (
                    <>
                      <tr>
                        <td className={styles.labelCell}>
                          <strong>Accomplishments:</strong>
                        </td>
                        <td className={styles.valueCell}>
                          <ul className={styles.accomplishmentsList}>
                            {levelDetails[activeLevelID]?.accomplishments.map((accomplishment) => (
                              <li key={accomplishment.ag_id} className={styles.accomplishmentItem}>
                                {accomplishment.name}
                                {mode === "owner/instructor" && (
                                  <a
                                    href="#"
                                    className={styles.revokeLink}
                                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                                      e.preventDefault();
                                      handleRevokeAccomplishment(accomplishment.ag_id);
                                    }}
                                  >
                                    (revoke)
                                  </a>
                                )}
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    </>
                  )}
                  <tr>
                    <td colSpan={2} className={styles.sectionHeaderCell}>
                      <div className={styles.skillsProgress}>
                        <strong>Skills:</strong>
                        <div className={styles.progressContainer}>
                          <div 
                            className={styles.progressBar} 
                            style={{ 
                              width: `${((levelDetails[activeLevelID]?.completed_skills_count || 0) / 
                                (levelDetails[activeLevelID]?.skills_count || 1)) * 100}%` 
                            }} 
                          />
                        </div>
                        <span className={styles.progressText}>
                          ({levelDetails[activeLevelID]?.completed_skills_count} of {levelDetails[activeLevelID]?.skills_count})
                        </span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2} className={styles.fullWidthCell}>
                      <ul className={styles.skillsGrid}>
                        {(levelDetails[activeLevelID]?.skills || []).map((skill) => (
                          <li key={skill.skill_id} className={styles.skillItem}>
                            <label 
                              className={styles.skillLabel}
                            >
                              <input
                                type="checkbox"
                                className={styles.checkbox}
                                checked={skill.status === "complete"}
                                disabled={mode === "member"}
                                onChange={(e) => handleSkillToggle(activeLevelID, skill.skill_id, e.target.checked)}
                              />
                              {skill.name}
                              {skill.status === "complete" && skill.completed_at && skill.actor_username && (
                                <span className={styles.skillTooltip}>
                                  Granted {formatDate(skill.completed_at)} by {skill.actor_username}
                                </span>
                              )}
                            </label>
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2} className={styles.sectionHeaderCell}>
                      <strong>Attendances:</strong>{" "}
                      {levelDetails[activeLevelID]?.attendances !== undefined
                        ? levelDetails[activeLevelID]?.attendances
                        : "N/A"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <p>No active level found for this curriculum.</p>
          )}
        </div>
      )}

      {mode === "owner/instructor" && (
        <div className={styles.notesSection}>
          <h3 className={styles.sectionTitle}>Most Recent Notes</h3>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.tableHeader}>Date</th>
                <th className={`${styles.tableHeader} ${styles.authorColumn}`}>Author</th>
                <th className={styles.tableHeader}>Note</th>
              </tr>
            </thead>
            <tbody>
              {notes.length > 0 ? (
                notes.slice(0, 3).map((note) => (
                  <tr key={note.note_id}>
                    <td className={styles.tableCell}>{new Date(note.created_at).toLocaleDateString()}</td>
                    <td className={`${styles.tableCell} ${styles.authorColumn}`}>{note.author_username}</td>
                    <td className={styles.tableCell}>
                      <div className={styles.noteText}>
                        {note.note.split('\n').map((line, i) => (
                          <React.Fragment key={i}>
                            {line}
                            {i < note.note.split('\n').length - 1 && <br />}
                          </React.Fragment>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className={styles.tableCell}>No notes found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MemberDetailsPage;
